/** Дропдаун профиля. Открывается по клику на аватар.
 *
 *  Содержит:
 *  - Шапку с аватаром, ФИО/email, ролью, активным Account
 *  - Пункт «Профиль» (редактирование) — для всех
 *  - Пункт «Управление аккаунтом» — только для ACCOUNT_OWNER
 *  - Кнопка «Выйти»
 */
import { Show, type Component, type JSX, createSignal, onCleanup, onMount } from "solid-js";
import { A, useNavigate } from "@solidjs/router";
import { Building2, Copy, LifeBuoy, LogOut, Mail, Phone, UserCog, X } from "lucide-solid";
import { useAuth } from "~/lib/auth";
import { Avatar } from "~/components/ui/Avatar";
import { useI18n } from "~/i18n";

/** Контакты техподдержки. Пока хардкод; со временем переедет в `Account.settings`
 *  или централизованную конфигурацию платформы. Описание и подпись локализованы
 *  через i18n, реальные email/phone — это данные, не переводим. */
const SUPPORT_CONTACTS = {
  email: "support@ritmflow.ru",
  phone: "+7 (800) 555-90-77",
};

export const ProfileMenu: Component = () => {
  const { meQuery, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [open, setOpen] = createSignal(false);
  const [supportOpen, setSupportOpen] = createSignal(false);
  let rootRef: HTMLDivElement | undefined;

  const onDocClick = (e: MouseEvent) => {
    if (rootRef && !rootRef.contains(e.target as Node)) setOpen(false);
  };
  onMount(() => {
    document.addEventListener("mousedown", onDocClick);
    onCleanup(() => document.removeEventListener("mousedown", onDocClick));
  });

  const user = () => meQuery.data?.user;
  const account = () => meQuery.data?.account;
  const role = () => meQuery.data?.membership?.role;
  const isOwner = () => role() === "account_owner";
  const roleLabel = (r: string) => {
    const key = `profileMenu.roles.${r}`;
    const translated = t(key);
    // Если ключа нет — `t` возвращает сам путь; покажем сырое значение из API.
    return translated === key ? r : translated;
  };

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/login", { replace: true });
  };

  const close = () => setOpen(false);

  return (
    <div ref={rootRef} class="relative">
      <button
        type="button"
        onClick={() => setOpen(!open())}
        aria-label={t("profileMenu.aria")}
        title={user()?.email ?? t("profileMenu.fallbackTitle")}
        class="flex h-9 w-9 items-center justify-center rounded-full p-0 transition-shadow hover:shadow-md"
      >
        <Avatar
          name={user()?.full_name}
          email={user()?.email ?? user()?.phone}
          style={user()?.avatar_style}
          size={32}
        />
      </button>

      <Show when={open()}>
        <div class="absolute right-0 top-full z-50 mt-1.5 w-[280px] overflow-hidden rounded-lg border border-border bg-surface text-ink shadow-lg">
          {/* Шапка с данными */}
          <div class="flex items-start gap-3 border-b border-border-faint px-4 py-3">
            <Avatar
              name={user()?.full_name}
              email={user()?.email ?? user()?.phone}
              style={user()?.avatar_style}
              size={40}
            />
            <div class="min-w-0 flex-1">
              <div class="truncate text-md font-semibold text-ink">
                {user()?.full_name || user()?.email || user()?.phone}
              </div>
              <Show when={user()?.email}>
                <div class="truncate text-sm text-ink-muted">{user()!.email}</div>
              </Show>
              <Show when={role()}>
                <div class="mt-1 inline-flex items-center rounded-full bg-primary-soft px-2 py-0.5 text-xs font-medium text-primary-soft-ink">
                  {roleLabel(role()!)}
                </div>
              </Show>
              <Show when={account()}>
                <div class="mt-1 truncate text-xs text-ink-muted">{account()!.name}</div>
              </Show>
            </div>
          </div>

          {/* Действия */}
          <div class="py-1">
            <A
              href="/profile"
              onClick={close}
              class="flex w-full items-center gap-3 px-4 py-2 text-md text-ink no-underline transition-colors hover:bg-surface-hover"
            >
              <UserCog size={16} class="text-ink-muted" />
              <span>{t("profileMenu.profile")}</span>
            </A>
            <Show when={isOwner()}>
              <A
                href="/account"
                onClick={close}
                class="flex w-full items-center gap-3 px-4 py-2 text-md text-ink no-underline transition-colors hover:bg-surface-hover"
              >
                <Building2 size={16} class="text-ink-muted" />
                <span>{t("profileMenu.accountManagement")}</span>
              </A>
            </Show>
            <button
              type="button"
              onClick={() => {
                close();
                setSupportOpen(true);
              }}
              class="flex w-full items-center gap-3 px-4 py-2 text-md text-ink transition-colors hover:bg-surface-hover"
            >
              <LifeBuoy size={16} class="text-ink-muted" />
              <span>{t("profileMenu.contactSupport")}</span>
            </button>
          </div>

          {/* Logout */}
          <div class="border-t border-border-faint py-1">
            <button
              type="button"
              onClick={handleLogout}
              class="flex w-full items-center gap-3 px-4 py-2 text-md text-danger transition-colors hover:bg-danger-soft"
            >
              <LogOut size={16} />
              <span>{t("profileMenu.logout")}</span>
            </button>
          </div>
        </div>
      </Show>

      <Show when={supportOpen()}>
        <SupportModal onClose={() => setSupportOpen(false)} />
      </Show>
    </div>
  );
};

/** Простая контактная модалка тех-поддержки. Реальная форма обратной связи
 *  (через email/HelpDesk-интеграцию) появится позже — пока показываем способы
 *  связаться руками и даём кнопки «скопировать». */
const SupportModal: Component<{ onClose: () => void }> = (props) => {
  const { t } = useI18n();
  const [copied, setCopied] = createSignal<"email" | "phone" | null>(null);
  const copy = async (text: string, kind: "email" | "phone") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      window.setTimeout(() => setCopied(null), 1500);
    } catch {
      /* ignore */
    }
  };
  return (
    <div
      class="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 p-4 pt-20"
      onClick={(e) => {
        if (e.target === e.currentTarget) props.onClose();
      }}
    >
      <div class="w-full max-w-md rounded-lg border border-border bg-surface p-5 shadow-xl">
        <div class="mb-3 flex items-center justify-between">
          <h2 class="flex items-center gap-2 text-lg font-semibold text-ink">
            <LifeBuoy size={18} class="text-ink-muted" />
            <span>{t("profileMenu.support.title")}</span>
          </h2>
          <button
            type="button"
            onClick={props.onClose}
            class="flex h-7 w-7 items-center justify-center rounded-md text-ink-muted hover:bg-surface-hover"
            aria-label={t("common.close")}
          >
            <X size={16} />
          </button>
        </div>
        <p class="mb-3 text-sm text-ink-muted">{t("profileMenu.support.description")}</p>
        <div class="space-y-2">
          <SupportRow
            icon={<Mail size={14} class="text-ink-muted" />}
            label={t("common.email")}
            value={SUPPORT_CONTACTS.email}
            href={`mailto:${SUPPORT_CONTACTS.email}`}
            copied={copied() === "email"}
            onCopy={() => copy(SUPPORT_CONTACTS.email, "email")}
          />
          <SupportRow
            icon={<Phone size={14} class="text-ink-muted" />}
            label={t("common.phone")}
            value={SUPPORT_CONTACTS.phone}
            href={`tel:${SUPPORT_CONTACTS.phone.replace(/[^+\d]/g, "")}`}
            copied={copied() === "phone"}
            onCopy={() => copy(SUPPORT_CONTACTS.phone, "phone")}
          />
        </div>
        <div class="mt-4 text-2xs text-ink-faint">
          {t("profileMenu.support.formHint")}
        </div>
      </div>
    </div>
  );
};

const SupportRow: Component<{
  icon: JSX.Element;
  label: string;
  value: string;
  href: string;
  copied: boolean;
  onCopy: () => void;
}> = (props) => {
  const { t } = useI18n();
  return (
    <div class="flex items-center gap-2 rounded-md border border-border-faint bg-surface-2 px-3 py-2">
      {props.icon}
      <div class="min-w-0 flex-1">
        <div class="text-2xs uppercase tracking-wide text-ink-faint">
          {props.label}
        </div>
        <a
          href={props.href}
          class="mono truncate text-sm text-ink hover:underline"
        >
          {props.value}
        </a>
      </div>
      <button
        type="button"
        onClick={props.onCopy}
        class="flex h-7 w-7 items-center justify-center rounded-md text-ink-2 hover:bg-surface-hover"
        title={props.copied ? t("common.copied") : t("common.copy")}
        aria-label={t("common.copy")}
      >
        <Show when={props.copied} fallback={<Copy size={12} />}>
          <span class="text-xs text-success">✓</span>
        </Show>
      </button>
    </div>
  );
};
